import dayjs from 'dayjs';
import { ReturnDocument, ReturnState } from 'models/returns';
import Link from 'next/link';

interface ReturnStatusBadgeProps {
  state: ReturnState;
  date?: string;
  arrivalWindow?: string;
  isRefunded?: boolean;
  returnId?: string;
  showReturnLink?: boolean;
  variant?: 'buyer' | 'seller' | 'warehouse';
  returnDoc: ReturnDocument;
}

export const ReturnStatusBadge = ({
  state,
  date,
  returnId,
  showReturnLink,
  variant,
  returnDoc,
}: ReturnStatusBadgeProps) => {
  switch (state) {
    case ReturnState.ReturnRequested:
      return (
        <div className="flex flex-col gap-1">
          <Link
            href={`/dashboard/${
              variant === 'buyer' ? 'returns' : 'seller-returns'
            }/${returnDoc.id}`}
            className="text-[2.2rem] font-bold text-brand-red"
          >
            Return Requested
          </Link>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? `Your return request has been submitted and is currently under
            review. We’ll notify you once it’s approved or if more information
            is needed.`
              : `A customer has requested a return. We’re currently reviewing the request to determine if it meets our return policy.`}
            {showReturnLink && (
              <Link
                href={`/dashboard/${
                  variant === 'buyer' ? 'returns' : 'seller-returns'
                }/${returnDoc.id}`}
                className="text-[1.6rem] font-medium underline"
              >
                View Return
              </Link>
            )}
          </span>
        </div>
      );
    case ReturnState.AwaitingDropoff:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-medium text-black">
            Return Awaiting Drop Off
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? `A pre-paid label has been provided. Print the label and drop off the package at the carrier listed on the label by ${dayjs(
                  returnDoc.created
                )
                  .add(3, 'day')
                  .format('MM/DD/YYYY')}.`
              : ' The return has been approved and the buyer has been issued a shipping label. They are required to ship the item within 3 business days.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.InTransit:
      return (
        <div className="flex flex-col gap-1">
          {date && (
            <span className="text-[2.2rem] font-bold text-brand-green">
              Arriving {date}
            </span>
          )}
          <span className="text-[2.2rem] font-medium text-black">
            Return In Transit
          </span>

          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'Your return has been shipped and is currently in transit.'
              : 'The return package is on its way back to you. You can track the shipment using the provided tracking number.'}{' '}
            {showReturnLink && (
              <>
                You can track its progress{' '}
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  here.
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.ReturnDelivered:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-black">
            Return Delivered
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'Your return has been delivered. Please allow 1–2 business days for processing and review.'
              : returnDoc.received_by_warehouse === 'yes'
              ? 'The item has been marked as received. Return is now pending final review and approval by MX Locker.'
              : returnDoc.received_by_warehouse === 'needs_attention'
              ? 'The item was marked as received, but an issue was reported. Return is now under review by MX Locker.'
              : 'The return has been delivered. Please inspect the item and confirm its condition so we can proceed with processing a refund.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.ReturnComplete:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-black">
            Return Complete
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'Your return has been processed and completed. Any applicable refund has been issued to your original payment method.'
              : 'The return process has been completed. Any applicable refunds or adjustments have been finalized.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.ReturnDenied:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-brand-red">
            Return Denied
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'Unfortunately, your return request has been denied. Please check your email for more information or reach out to support if you have any questions.'
              : 'The return request was reviewed and denied based on our return policy. No action is needed on your end.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.OnHold:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-brand-secondary">
            Return On Hold
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'Your return is currently on hold due to an issue that needs to be resolved. Please check your email or contact support for next steps.'
              : 'The return is currently on hold due to an issue (e.g., missing tracking, delivery dispute, or policy check). Please monitor for updates or reach out to support if needed.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    case ReturnState.ReturnCancelled:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-brand-secondary">
            Return Cancelled
          </span>
          <span className="text-[1.6rem] font-medium text-black">
            {variant === 'buyer'
              ? 'This return has been cancelled because the item was not scanned in by the carrier within 3 business days of the return label being issued, as required by our return policy.'
              : 'The return was cancelled due to the buyer not shipping the item within the required 3 business days. No further action is required.'}{' '}
            {showReturnLink && (
              <>
                <Link
                  href={`/dashboard/${
                    variant === 'buyer' ? 'returns' : 'seller-returns'
                  }/${returnDoc.id}`}
                  className="text-[1.6rem] font-medium underline"
                >
                  View Return
                </Link>
              </>
            )}
          </span>
        </div>
      );
    default:
      return (
        <div className="flex flex-col gap-1">
          <span className="text-[2.2rem] font-bold text-black">
            Unknown Status
          </span>
        </div>
      );
  }
};

export default ReturnStatusBadge;
